import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { Node } from 'reactflow';
import NodeData from '../../models/NodeData';
import FeatureType from '../../models/FeatureType';

export const getDefaultTitleFromNodeType = (nodeType: FeatureType): string => {
  const defaults: { [key in FeatureType]: string } = {
    [FeatureType.UTTER_NODE]: 'Aussage',
    [FeatureType.TOPIC_CHOICE_NODE]: 'Intent-Entscheider',
    [FeatureType.FAAS_CHOICE_NODE]: 'Fortgeschrittener Validierer',
    [FeatureType.DATE_CHOICE_NODE]: 'Datum-Entscheider',
    [FeatureType.TIME_DECISION_NODE]: 'Zeit-Entscheider',
    [FeatureType.NUMBER_CHOICE_NODE]: 'Zahl-Entscheider',
    [FeatureType.REGEX_CHOICE_NODE]: 'Regulärer-Ausdruck-Entscheider',
    [FeatureType.QUESTION_NODE]: 'Frage',
    [FeatureType.TEXT_CHOICE_NODE]: 'Text-Entscheider',
    [FeatureType.API_ACTION_NODE]: 'API-Call',
    [FeatureType.EMAIL_ACTION_NODE]: 'Mailversand',
    [FeatureType.USER_INPUT_NODE]: 'Benutzereingabe',
    [FeatureType.PHONE_FORWARDING_ACTION_NODE]: 'Telefonische Weiterleitung',
    [FeatureType.END_NODE]: 'Ende',
    [FeatureType.START_NODE]: 'Start',
    [FeatureType.ADD_NODE]: '',
  };

  return defaults[nodeType] || '';
};

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const removeWhitespacesFromNodeName = (
  nodeName: string,
): string => nodeName.replace(/\s+/g, '_');

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const removeNumbersFromNodeName = (
  nodeName: string,
): string => nodeName.replace(/[0-9]/g, '');

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const removeSpecialCharactersFromNodeName = (
  nodeName: string,
): string => nodeName.replace(/[^a-zA-Z_üäö]/g, '');

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const replaceUmlautsFromSlotName = (
  slotName: string, reverse = false,
): string => {
  let result = slotName;
  if (reverse) {
    result = result.replace('ae', 'ä');
    result = result.replace('ue', 'ü');
    result = result.replace('oe', 'ö');
    result = result.replace('ss', 'ß');
    return result;
  }
  result = result.replace('ä', 'ae');
  result = result.replace('ü', 'ue');
  result = result.replace('ö', 'oe');
  result = result.replace('ß', 'ss');
  return result;
};

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const addDuplicateCounterToSlotName = (
  nodes: Node<NodeData>[],
): Node<NodeData>[] => {
  const suffixes: { [key: string]: number } = {};
  const result: Node<NodeData>[] = [];

  nodes.forEach((node) => {
    const { slotName } = node.data;
    if (!slotName) return;
    if (!suffixes[slotName]) {
      suffixes[slotName] = 0;
    }

    suffixes[slotName] += 1;
    result.push({
      ...node,
      data: {
        ...node.data,
        slotName: `${slotName}_${suffixes[slotName]}`,
      },
    });
  });
  return result;
};

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const lowerCaseSlotName = (slotName: string): string => slotName.toLowerCase();

const addDialogIdToSlotName = (
  slotName: string, dialogID: number,
): string => `${slotName}_${dialogID}`;

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
export const resolveInitialNodeName = (
  nodeName: string | undefined, nodeType: FeatureType,
): string => {
  if (!nodeName
  || nodeType === FeatureType.START_NODE
  || nodeType === FeatureType.END_NODE) {
    return getDefaultTitleFromNodeType(nodeType);
  }
  return nodeName;
};

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
const humanReadableToMachineInterpretable = (
  dialogID: number, dialogName: string, nodes: Node<NodeData>[],
): Node<NodeData>[] => nodes;

/**
 * General component description in JSDoc format. Markdown is *supported*.
 */
export const getSlotNameFromNodeName = (
  dialogID: number,
  dialogName: string,
  nodes: Node<NodeData>[],
  selectedNode: Node<NodeData>,
): string => {
  const miNodes = humanReadableToMachineInterpretable(
    dialogID,
    dialogName,
    nodes,
  );
  const selectedFilteredNode = miNodes.find(
    (node) => node.id === selectedNode.id,
  );
  return selectedFilteredNode?.data?.slotName || '';
};

// eslint-disable-next-line max-len
const germanPrepositions = ['ab', 'abzüglich', 'abseits', 'an', 'anhand', 'angesichts', 'anstatt', 'anstelle', 'auf', 'aufgrund', 'aufgrund von', 'aus', 'ausgenommen', 'ausschließlich', 'ausschließlich von', 'ausser', 'ausserhalb', 'beiderseits', 'bezüglich', 'betreffs', 'bewusst', 'bis', 'dank', 'diesseits', 'durch', 'einwärts', 'eingangs', 'entgegen', 'entlang', 'entsprechend', 'entsprechend', 'entzogen', 'fern', 'folgend', 'für', 'gegen', 'gegenüber', 'gedenken', 'gemäß', 'gemäß', 'genüber', 'halber', 'hinsichtlich', 'hinter', 'innerhalb', 'innerhalb von', 'in', 'infolge', 'infolge', 'inmitten', 'jenseits', 'jenseits von', 'je', 'kraft', 'laut', 'längs', 'links', 'mithilfe', 'mithilfe von', 'mittels', 'mitten', 'mit', 'nach', 'nachfolgend', 'nahe', 'nahezu', 'nämlich', 'neben', 'nebst', 'nebst', 'nördlich', 'ohne', 'ohne dass', 'ohnehin', 'östlich', 'quer', 'rechts', 'rund', 'samt', 'samt', 'seit', 'seitens', 'seitwärts', 'soweit', 'südlich', 'statt', 'trotz', 'über', 'um', 'unabhängig', 'unfern', 'ungeachtet', 'ungeachtet dessen', 'ungerade', 'unbeschadet', 'unweit', 'unweit von', 'unter', 'unterhalb', 'untertage', 'unterwegs', 'vergangen', 'vermittels', 'vollends', 'vom', 'von', 'vonseiten', 'vor', 'vorbei', 'vorbehaltlich', 'während', 'wegen', 'wegen', 'westlich', 'wider', 'zu', 'zufolge', 'zufolge', 'zufolge von', 'zugunsten', 'zulasten', 'zuliebe', 'zum', 'zur', 'zurück', 'zurückzuführen', 'zurückzahlen', 'zusammen', 'zusätzlich', 'zuvor', 'zwecks', 'zwischen'];

const rudimentaryGermanPrepositionCheck = (
  word: string,
): boolean => germanPrepositions.includes(word.toLowerCase());

export const machineInterpretableToHumanReadable = (
  slotName: string,
  dialogName: string,
): string => {
  let result = slotName;

  let dName = dialogName;
  dName = removeWhitespacesFromNodeName(dName);
  dName = removeNumbersFromNodeName(dName);
  dName = removeSpecialCharactersFromNodeName(dName);
  dName = replaceUmlautsFromSlotName(dName);
  dName = lowerCaseSlotName(dName);

  result = result
    .replace(/_\d+_\d+$/, '')
    .replace(`${dName}_`, '')
    .split('_')
    .map((word, index) => {
      if (index === 0
        || (index > 0 && !rudimentaryGermanPrepositionCheck(word))) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
  return result;
};
